import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import buildingsApi from '../../APIs/buildings';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import {
  Button,
  PageHeader,
  Pagination,
  Table,
  Typography,
} from '../../designComponents';
import Filter from '../../designComponents/Filter';
import { TableDataType } from '../../designComponents/Table';
import { ADD_BUILDING_ROUTE } from '../../routes/routePaths';
import Store from '../../store';
import { RootState } from '../../store/selectors';
import theme from '../../theme';
import { PaginationType } from '../../types';
import { humanReadableDate, requestAPI } from '../../utils';
import { BuildingContainerStyled } from './style';

export type BuildingType = {
  _id: string;
  buildingName: string;
  createdAt: string;
  buildingAddress: string;
  notes: string;
  bluePrints: {
    _id: string;
    name?:string,
    bluePrintUrl: string;
    title: string;
    thumbnailUrl?: string;
  }[];
};

export type MyParamsType = PaginationType & { search?: string };

const Building = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const pagination = useSelector(
    (state: RootState) => state.buildings.pagination,
  );

  const buildings: BuildingType[] = useSelector(
    (state: RootState) => state.buildings.data,
  );

  const handleGetBuildings = async (myParams: MyParamsType) => {
    try {
      setLoading(true);
      await requestAPI(
        buildingsApi.getBuildings({
          page: myParams.page,
          limit: myParams.limit,
          ...(myParams?.search ? { search: myParams.search } : {}),
        }),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFunction = useCallback(
    debounce((myParams) => handleGetBuildings(myParams), 500),
    [],
  );

  useEffect(() => {
    handleGetBuildings(pagination);
  }, []);

  const handlePagination = (updatedParams: PaginationType) => {
    handleGetBuildings(updatedParams);
    dispatch(Store.Actions.update('buildings.pagination', updatedParams));
  };

  const handleOnSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debouncedFunction({ ...pagination, page: 1, search: value });
  };
  const handleAddBuilding = () => navigate(ADD_BUILDING_ROUTE);
  const tableData: TableDataType = {
    headings: [
      { key: 'date', label: 'Created Date' },
      { key: 'name', label: 'Building Name' },
      {
        key: 'address',
        label: 'Address of Property',
      },

      {
        key: 'notes',
        label: 'Notes',
      },
      {
        key: 'actions',
        label: 'Action',
      },
    ],
    body: Array.isArray(buildings)
      ? buildings?.map((building) => {
        return {
          id: building._id,
          date: humanReadableDate(building.createdAt),
          name: building.buildingName,
          address: building.buildingAddress,
          notes: building.notes,
          actions: (
              <Button
                variant="secondary"
                onClick={() => navigate(building._id)}
                style={{
                  backgroundColor: theme.colors.blueLight,
                  border: `1px solid ${theme.colors.blueLight}`,
                }}
              >
                View Building
              </Button>
          ),
        };
      })
      : [],
  };
  return (
    <BuildingContainerStyled>
      <div className="header">
        <PageHeader
          pageTitle={<Typography variant="title">Building List</Typography>}
          otherActions={
            <Button variant="secondary" size="sm" onClick={handleAddBuilding}>
              <PlusIcon className="icon" />
              Create Building
            </Button>
          }
          handleOnSearchTextChange={handleOnSearchTextChange}
          search={search}
          filterContent={
            <Filter label1="building Name" label2="Address Name" />
          }
          width="200px"
        />
      </div>

      <div className="table-container">
        <Table
          tableData={tableData}
          maxWidthForCardView="800px"
          isLoading={loading}
        />
      </div>

      <Pagination
        activePage={pagination.page}
        handlePagination={handlePagination}
        limit={pagination.limit}
        totalCount={pagination.totalData || 0}
      />
    </BuildingContainerStyled>
  );
};

export default Building;
